/** Página Status: el estado de la plataforma medido en vivo (GET /api/public/estado). */
import { useEffect, useState } from 'react';
import Marco, { Navegacion, useMeta } from './Marco';
import { useT, useIdiomaSitio } from './idioma';
import { Check, AlertTriangle } from 'lucide-react';
import { leer } from './planes';

const COLOR = { operativo: '#3FA36B', degradado: '#F4A01D', caido: '#D0493B' };

export default function Estado() {
  const T = useT();
  const t = T.estado;
  const idioma = useIdiomaSitio(s => s.idioma);
  useMeta(T.meta.estado);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let vivo = true;
    const cargar = () => leer('estado').then(d => { if (vivo) { setDatos(d); setError(false); } }).catch(() => { if (vivo) setError(true); });
    cargar();
    const id = setInterval(cargar, 60000);
    return () => { vivo = false; clearInterval(id); };
  }, []);

  const general = datos?.general || 'operativo';
  const hora = datos ? new Date(datos.revisado).toLocaleTimeString(idioma === 'es' ? 'es-CR' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';
  const grupo = (g) => (datos?.servicios || []).filter(s => s.grupo === g);

  return (
    <Marco>
      <div style={{ background: '#fff', borderBottom: '1px solid #E2E5EF' }}><Navegacion tono="clara" /></div>
      <main style={{ background: '#F4F5FA' }}>
        <div className="caja" style={{ maxWidth: 980, paddingTop: 72, paddingBottom: 110 }}>
          <div className="ojo">{t.ojo}</div>
          <h1 className="display" style={{ fontWeight: 800, fontSize: 'clamp(36px,4.4vw,56px)', margin: '14px 0 0', color: '#1A1D4E' }}>{t.titulo}</h1>
          <p className="suave" style={{ fontSize: 18, margin: '16px 0 0', maxWidth: 720 }}>{t.bajada}</p>

          <div className="estado-cab" style={{ marginTop: 40 }} aria-live="polite">
            {!datos && !error && <span className="suave">{t.cargando}</span>}
            {error && !datos && <span style={{ color: '#922E23' }}>{t.error}</span>}
            {datos && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: '50%', flex: '0 0 40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: general === 'operativo' ? '#E7F6EF' : general === 'degradado' ? '#FCF8EE' : '#F9F1F0', color: COLOR[general] }}>
                    {general === 'operativo' ? <Check size={20} strokeWidth={2.6} /> : <AlertTriangle size={19} />}
                  </span>
                  <div>
                  <div className={`display est-${general}`} style={{ fontWeight: 700, fontSize: 20, letterSpacing: '-.01em' }}>{t.general[general]}</div>
                  <div className="suave" style={{ fontSize: 15 }}>{t.generalTexto[general]}</div>
                  </div>
                </div>
                <div className="mono suave" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><span className="punto est-operativo" />{t.vivo} · {hora}</div>
              </>
            )}
          </div>

          {datos?.actividad?.length > 0 && (
            <section style={{ marginTop: 44 }}>
              <h2 className="display" style={{ fontWeight: 700, fontSize: 20, color: '#171A26' }}>{t.actividad}</h2>
              <div style={{ marginTop: 14, background: '#fff', border: '1px solid #E2E5EF', borderRadius: 16, padding: '22px 22px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 72 }} role="img" aria-label={t.actividad}>
                  {datos.actividad.map(d => <span key={d.fecha} title={d.fecha} style={{ flex: 1, minWidth: 3, height: `${Math.max(4, d.nivel * 100)}%`, background: d.nivel ? '#4652B8' : '#E2E5EF', borderRadius: 2 }} />)}
                </div>
                <div className="mono suave" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginTop: 8 }}><span>{datos.actividad[0].fecha}</span><span>{datos.actividad[datos.actividad.length - 1].fecha}</span></div>
                <p className="suave" style={{ fontSize: 13, margin: '10px 0 0' }}>{t.actividadNota}</p>
              </div>
            </section>
          )}

          {datos && [['propios', t.propios], ['dependencias', t.dependencias]].map(([g, titulo]) => (
            <section key={g} style={{ marginTop: 44 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <h2 className="display" style={{ fontWeight: 700, fontSize: 20, color: '#171A26' }}>{titulo}</h2>
                <span className="mono suave" style={{ fontSize: 12 }}>{t.ultimos(grupo(g).length)}</span>
              </div>
              <div className="servicios">
                {grupo(g).map(s => {
                  const [nombre, desc] = t.servicios[s.id] || [s.id, ''];
                  return (
                    <div key={s.id} className="servicio">
                      <div style={{ flex: '1 1 260px', minWidth: 0 }}><div style={{ fontWeight: 600 }}>{nombre}</div><div className="suave" style={{ fontSize: 14 }}>{desc}</div></div>
                      <div aria-hidden="true" style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 24, flex: '0 1 300px', minWidth: 160 }}>
                        {(s.dias || []).map(d => <span key={d.fecha} title={`${d.fecha} · ${d.estado ? t.estados[d.estado] : t.sinDatos}`} style={{ flex: '1 1 0', minWidth: 3, height: '100%', borderRadius: 2, background: d.estado ? COLOR[d.estado] : '#E2E5EF' }} />)}
                      </div>
                      <div className={`est-${s.estado}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, fontWeight: 500, fontSize: 14, flex: '0 0 120px' }}><span className="punto" />{t.estados[s.estado] || s.estado}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {datos && (
            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 22, fontSize: 13, color: '#6B7289', alignItems: 'center' }}>
              {['operativo', 'degradado', 'caido'].map(e => <span key={e} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: COLOR[e] }} />{t.estados[e]}</span>)}
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#E2E5EF' }} />{t.sinDatos}</span>
              <span style={{ flexBasis: '100%' }}>{t.historialNota}</span>
            </div>
          )}

          <div style={{ marginTop: 44, background: '#ECEEFA', borderRadius: 16, padding: '24px 26px', color: '#2C3380' }}>
            <div className="display" style={{ fontWeight: 700, fontSize: 17 }}>{t.nota}</div>
            <p style={{ margin: '6px 0 0', fontSize: 15.5 }}>{t.notaTexto}</p>
          </div>
        </div>
      </main>
    </Marco>
  );
}
